/** @jsxImportSource @emotion/react */
import {
  Device,
  DeviceType,
  FirmwareVersionProps,
  RegisterStatus,
  trans,
  TranslationGroup,
  TranslationKey,
} from 'lib/types'
import { useState, useEffect } from 'react'
import { DetailSliderContent, EditElementModal, ConfirmationDialog } from 'components/partials'
import { getBatteryDisplay, getHealthIcon } from 'components/partial-pages/ButtonsDevicesPage'
import useModal from 'lib/hooks/useModal'
import { ActionButton, ConditionalRender, StyledDialogActions } from 'components/widgets'
import { ButtonDetail, DetailRow, FirmwareDetail, WanDetail } from './utils'
import { formatDistance, getHealth, isWan, parseTime, toDate } from 'lib/utils'
import { format } from 'date-fns'
import { Devices, Orgs } from 'models'
import { isAdmin, isFleetVoiceOrg, isInstaller, isSuperAdmin } from 'lib/utils/auth'
import { actionRowStyles, detailStyles } from 'styles'
import { unregisterAlertSource, updateAlertSource, disableAlertSource, updateAlertSourcePartial } from 'models/api'
import { useGlobal } from 'contexts/GlobalProvider'
import { ALL_ORGS_SELECTED, getAppColors } from 'lib/constants'
import { Dialog, DialogContent, Select, MenuItem, FormControl, FormControlLabel } from '@material-ui/core'
import { StyledDialogTitle } from 'components/legacy-poorly-defined/Common'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { ToggleSwitch, SwitchLabel } from 'components/partials/MappedInIntegrationForm'
const DG_HEIGHT = 36

// NEW: device group API helpers
import { getDeviceGroups, setAlertSourceDeviceGroup } from 'models/api/deviceGroups'

/* -------------------------------------------------------------------------- */
/* Helpers: display label = "channel - language"                               */
/* -------------------------------------------------------------------------- */

function toLangLabel(code?: string): string {
  const c = String(code || '')
    .trim()
    .toLowerCase()
  if (!c) return ''
  if (c === 'en') return 'English'
  if (c === 'es') return 'Spanish'
  if (c === 'fr') return 'French'
  if (c === 'de') return 'German'
  // tolerate "EN"/"ES"/etc
  if (c === 'en'.toUpperCase().toLowerCase()) return 'English'
  return code || ''
}

function deviceGroupDisplayLabel(g: any): string {
  // Prefer: "channelName - Language"
  const channel = String(g?.channelName || '').trim()
  const lang = toLangLabel(g?.language)
  const both = [channel, lang].filter(Boolean).join(' - ')
  if (both) return both

  // fallbacks so UI never breaks
  const n = String(g?.name || '').trim()
  if (n) return n
  return channel || '—'
}

export const DeviceDetailDrawer = ({ device, close }: { device: Device; close: Function }) => {
  const translation: TranslationGroup = trans.merge(TranslationKey.DEVICES_DRAWER)
  const healthLabels: TranslationGroup = trans.merge(TranslationKey.HEALTH_LABELS)

  const [openUnregisterModal, setOpenUnregisterModal] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [renameModalOpen, setRenameModalOpen] = useState(false)

  // --- Device Group state ---
  const [deviceGroupId, setDeviceGroupId] = useState<string>(device.deviceGroupId ?? '')
  const [savingGroup, setSavingGroup] = useState(false)

  // --- Single Press toggle state ---
  const [singlePress, setSinglePress] = useState<boolean>(!!device.singlePress)
  const [savingSinglePress, setSavingSinglePress] = useState(false)
  // The button caches its activation settings and only picks up the new ones
  // when it reconnects, so between the save and a power cycle a press (or a
  // health check) still fires a single-press alert. Reminds the user to power
  // cycle it; see the dialog at the bottom of this file.
  const [showPowerCycleNotice, setShowPowerCycleNotice] = useState(false)

  useEffect(() => {
    setSinglePress(!!device.singlePress)
  }, [device.singlePress])

  // The reminder belongs to the device it was raised for; reusing the drawer
  // for another device must not carry it over.
  useEffect(() => {
    setShowPowerCycleNotice(false)
  }, [device.id])

  const handleToggleSinglePress = async (_: unknown, checked: boolean) => {
    if (savingSinglePress) return
    const previous = singlePress
    setSinglePress(checked)
    setSavingSinglePress(true)
    if (checked) setShowPowerCycleNotice(false)
    try {
      await updateAlertSourcePartial(device.id, { singlePress: checked })
      // Only after the save lands, and only for the off direction — turning it
      // back on needs no power cycle to stay safe.
      setShowPowerCycleNotice(!checked)
      setRefreshFlag(true)
    } catch {
      setSinglePress(previous)
      setShowPowerCycleNotice(false)
    } finally {
      setSavingSinglePress(false)
    }
  }

  // NOTE: we keep `name` in state for compatibility, but it will now be "channel - language"
  const [deviceGroups, setDeviceGroups] = useState<Array<{ id: string; name: string }>>([])

  const deviceOrgId = device.orgId
  const orgsById = Orgs.use(({ orgsById }) => orgsById)
  const org = orgsById[deviceOrgId]
  const { selectedOrgId, selectedOrg, setRefreshFlag, singlePressActivationEnabled } = useGlobal()

  // Hidden by default; only shown once a superadmin enables it for the session
  // via the org details page (singlePressActivationEnabled), on top of the
  // existing per-device requirements (WAN button + wanPeerless2App firmware).
  const showSinglePressToggle =
    singlePressActivationEnabled &&
    isSuperAdmin() &&
    device.deviceType === DeviceType.WAN &&
    !!device.props?.version?.wanPeerless2App

  // Keep select in sync when drawer opens for a different device
  useEffect(() => {
    setDeviceGroupId(device.deviceGroupId ?? '')
  }, [device.deviceGroupId])

  // Fetch device groups for current org
  useEffect(() => {
    let ignore = false
    ;(async () => {
      try {
        const orgIdForFetch = selectedOrgId && selectedOrgId !== ALL_ORGS_SELECTED ? selectedOrgId : device.orgId
        const groups = await getDeviceGroups(orgIdForFetch)

        const normalized = [
          { id: '', name: '— No group —' },
          ...groups.map(g => ({
            id: String((g as any).id),
            // ✅ new label: "channel - language"
            name: deviceGroupDisplayLabel(g),
          })),
        ]

        if (!ignore) setDeviceGroups(normalized)
      } catch {
        if (!ignore) setDeviceGroups([{ id: '', name: '— No group —' }])
      }
    })()
    return () => {
      ignore = true
    }
  }, [selectedOrgId, device.orgId])

  async function handleUnregister() {
    const orgId = selectedOrgId && selectedOrgId === ALL_ORGS_SELECTED ? '' : selectedOrgId
    await unregisterAlertSource(device.id, orgId || '')
    setRefreshFlag(true)
    close()
  }

  async function handleDelete() {
    await disableAlertSource(device.id)
    setRefreshFlag(true)
    close()
  }

  const showUnregister = isAdmin(selectedOrgId || '') || isInstaller(selectedOrgId || '')
  const showEditName = isAdmin(selectedOrgId || '')

  const rightStyles = {
    display: 'flex',
    alignItems: 'center',
    lineHeight: '1.0rem',
  } as const

  const padRightStyles = {
    paddingRight: '10px',
  } as const

  const batteryWrapperStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: '64px',
  } as const

  const DG_HEIGHT = 40 // keep label vertically centered with the select

  const health = getHealth(device)
  const healthRow = (
    <div css={rightStyles}>
      <div css={padRightStyles}>{getHealthIcon(health)}</div>
      <div>{healthLabels[health]}</div>
    </div>
  )

  const showFirmware = device.props?.version || device.props?.app?.version
  const preciseDate = device.lastSeenAt ? format(toDate(parseTime(device.lastSeenAt)), 'M/d/yyyy pp') : ''
  const pushEnabled = device.pushEnabled ? translation.yes : translation.no

  // Handle group change (add/remove per Swagger)
  const handleChangeGroup = async (nextId: string) => {
    if (nextId === deviceGroupId) return
    setSavingGroup(true)
    try {
      await setAlertSourceDeviceGroup({
        currentGroupId: deviceGroupId || null,
        nextGroupId: nextId || null,
        alertSourceId: device.id,
        orgId: selectedOrgId || device.orgId || '',
      })
      setDeviceGroupId(nextId)
      setRefreshFlag(true)
    } finally {
      setSavingGroup(false)
    }
  }

  // deviceGroupSelect: tighten height + center
  const deviceGroupSelect = (
    <div css={rightStyles}>
      <FormControl
        disabled={!showEditName || savingGroup}
        style={{
          minWidth: 220, // slightly wider for "channel - language"
          borderRadius: 16,
          background: getAppColors('--color-surface-2'),
          padding: '2px 10px',
          height: DG_HEIGHT,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Select
          id="device-group-selector"
          value={deviceGroupId}
          onChange={(e: React.ChangeEvent<{ value: unknown }>) => handleChangeGroup(e.target.value as string)}
          IconComponent={KeyboardArrowDownIcon}
          disableUnderline
          displayEmpty
          renderValue={(value: unknown) => {
            const v = String(value ?? '')
            const label = deviceGroups.find(g => g.id === v)?.name ?? '— No group —'
            return <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>{label}</span>
          }}
          MenuProps={{
            getContentAnchorEl: null,
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            PaperProps: { style: { borderRadius: 12 } },
          }}
        >
          {deviceGroups.map(g => (
            <MenuItem key={g.id} value={g.id} style={{ textAlign: 'center' }}>
              {g.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  )

  return (
    <>
      <DetailSliderContent
        title={(device.responder ? translation.Responder : translation.Device) + ' ' + translation.Details}
        close={close}
      >
        <ul css={detailStyles}>
          {org && <DetailRow title={translation.Property} content={org.name} />}
          <DetailRow title={translation.Property_ID} content={device.orgId} />
          {/* **REDUNDANT** <DetailRow title={translation.Health} content={healthRow} /> */}
          <DetailRow title={translation.Registration_Status} content={translation[device.registration]} />
          <DetailRow
            title={translation.registered}
            // parseTime falls back to now; a missing date must not read as registered today.
            content={device.registeredAt ? format(toDate(parseTime(device.registeredAt)), 'M/d/yyyy') : '-'}
          />
          <DetailRow title={translation.Type} content={device.deviceType} />
          <DetailRow title={translation.Name} content={device.name} />
          {isWan(device.deviceType) && (
            <>
              {Number.isInteger(device.batteryPercent) && (
                <DetailRow
                  title={translation.Battery}
                  content={
                    Number.isInteger(device.batteryPercent) ? (
                      <div css={batteryWrapperStyles}>{getBatteryDisplay(device)}</div>
                    ) : (
                      ''
                    )
                  }
                />
              )}
              <WanDetail data={device.props} />
            </>
          )}
          {showFirmware && (
            <FirmwareDetail
              fallback={''}
              title={translation.Firmware}
              update={device.updateFirmware}
              version={{
                ...device.props?.version,
                ...(device.deviceType === 'ios' ||
                device.deviceType === 'android' ||
                device.deviceType === 'voice_button'
                  ? { [device.deviceType]: device.appVersion || '' }
                  : {}),
              }}
            />
          )}
          {device.responder && <DetailRow title={translation.Push_Enabled} content={pushEnabled} />}
          {device.props?.button && <ButtonDetail data={device.props?.button} update={device.updateFirmware} />}
          <DetailRow title={translation.Last_Check_In} content={preciseDate} />
          <DetailRow title={isWan(device.deviceType) ? translation.IMEI : translation.Device_ID} content={device.id} />
          {device.props?.app?.iccid && <DetailRow title="ICCID" content={device.props.app.iccid} />}
          {device.props?.app?.ipAddress && <DetailRow title="IP Address" content={device.props.app.ipAddress} />}
          {device.props?.app?.wirelessMode && (
            <DetailRow title="Wireless Mode" content={device.props.app.wirelessMode} />
          )}
          {device.props?.app?.currentUptime !== undefined && !isNaN(Number(device.props.app.currentUptime)) && (
            <DetailRow
              title={translation.uptime}
              content={formatDistance(Number(device.props.app.currentUptime) * 1000)}
            />
          )}
          {device.wifiMac && <DetailRow title="WiFi MAC" content={device.wifiMac} />}
          <ConditionalRender condition={device.deviceType === DeviceType.VOICE && (!!selectedOrg?.zelloUsername || isFleetVoiceOrg(selectedOrg))}>
            <DetailRow title="Device Group" content={deviceGroupSelect} />
          </ConditionalRender>
          {showSinglePressToggle && (
            <DetailRow
              title="Single Press Activation"
              content={
                <div css={rightStyles}>
                  <FormControlLabel
                    style={{ marginRight: 0 }}
                    control={
                      <ToggleSwitch
                        checked={singlePress}
                        onChange={handleToggleSinglePress}
                        name="singlePress"
                        disabled={savingSinglePress || !showEditName}
                      />
                    }
                    label={<SwitchLabel>{singlePress ? 'Enabled' : 'Disabled'}</SwitchLabel>}
                  />
                </div>
              }
            />
          )}
        </ul>

        <div css={actionRowStyles}>
          {showEditName && (
            <ActionButton onClick={() => setRenameModalOpen(true)}>{translation.Edit_Device_Name}</ActionButton>
          )}
          {showUnregister && (
            <ActionButton onClick={() => setOpenUnregisterModal(true)}>{translation.Unregister_Device}</ActionButton>
          )}
        </div>

        <EditElementModal
          open={renameModalOpen}
          title={translation.Edit_Device_Name}
          element={device}
          fieldName={'name'}
          close={() => setRenameModalOpen(false)}
          action={updateAlertSource}
        />

        <ConfirmationDialog
          title={translation.Unregister_Device}
          description={translation.confirm_unregister}
          open={openUnregisterModal}
          onConfirm={() => {
            setOpenUnregisterModal(false)
            handleUnregister()
          }}
          handleClose={() => setOpenUnregisterModal(false)}
        />
        {/* Informational only: there is nothing to confirm, so the single button
            just dismisses it. Kept out of the drawer body so the reminder text
            cannot stretch the drawer. */}
        <Dialog open={showPowerCycleNotice} onClose={() => setShowPowerCycleNotice(false)} maxWidth="xs" fullWidth>
          <StyledDialogTitle>{translation.power_cycle_button}</StyledDialogTitle>
          <DialogContent>{translation.power_cycle_notice}</DialogContent>
          <StyledDialogActions>
            <ActionButton width="110px" height="28px" onClick={() => setShowPowerCycleNotice(false)}>
              {translation.ok}
            </ActionButton>
          </StyledDialogActions>
        </Dialog>

        <ConfirmationDialog
          title={translation.Delete_Device}
          description={translation.confirm_delete}
          open={openDeleteModal}
          onConfirm={() => {
            setOpenDeleteModal(false)
            handleDelete()
          }}
          handleClose={() => setOpenDeleteModal(false)}
        />
      </DetailSliderContent>
    </>
  )
}
